/**
 * Client-side helpers for the reservation slot-block lease countdown.
 * Server truth remains Valkey; these guard UI gating and timers only.
 */

/** Proactive server sync before local countdown hits zero (clock skew + network latency). */
export const SLOT_BLOCK_LEASE_REFRESH_THRESHOLD_SEC = 10

export function shouldRefreshSlotBlockLease(remainingSec: number | null): boolean {
  if (remainingSec === null) {
    return false
  }
  return remainingSec <= SLOT_BLOCK_LEASE_REFRESH_THRESHOLD_SEC
}

export function parseSlotBlockExpiresAtMs(expiresAt: string | null): number | null {
  if (expiresAt === null || expiresAt === '') {
    return null
  }
  const ms = Date.parse(expiresAt)
  if (!Number.isFinite(ms)) {
    return null
  }
  return ms
}

export function isSlotBlockLeaseActive(expiresAt: string | null, now = Date.now()): boolean {
  const endMs = parseSlotBlockExpiresAtMs(expiresAt)
  if (endMs === null) {
    return false
  }
  return endMs > now
}

export function slotBlockRemainingSec(expiresAt: string | null, now = Date.now()): number | null {
  const endMs = parseSlotBlockExpiresAtMs(expiresAt)
  if (endMs === null) {
    return null
  }
  return Math.max(0, Math.floor((endMs - now) / 1000))
}

/** True when the persisted/in-memory lease matches the effective slot and is still valid. */
export function isSlotBlockLeaseReadyForSlot(
  expiresAt: string | null,
  blockedSlotId: string | null,
  effectiveSlotId: string | null,
  now = Date.now(),
): boolean {
  if (effectiveSlotId === null || effectiveSlotId === '') {
    return false
  }
  if (blockedSlotId === null || blockedSlotId === '') {
    return false
  }
  return blockedSlotId === effectiveSlotId && isSlotBlockLeaseActive(expiresAt, now)
}

export type SlotBlockTimerDisplayInput = {
  effectiveSlotId: string | null
  blockRemainingSec: number | null
  slotBlockExpiresAt: string | null
  leaseExpiredNotice: boolean
  isConfirmationView: boolean
}

/** Show the slot-block countdown only when server lease is active and no expiry banner. */
export function shouldShowSlotBlockTimer(
  input: SlotBlockTimerDisplayInput,
  now = Date.now(),
): boolean {
  if (input.isConfirmationView) {
    return false
  }
  if (input.leaseExpiredNotice) {
    return false
  }
  if (input.effectiveSlotId === null || input.effectiveSlotId === '') {
    return false
  }
  if (input.blockRemainingSec === null || input.blockRemainingSec <= 0) {
    return false
  }
  return isSlotBlockLeaseActive(input.slotBlockExpiresAt, now)
}

export function shouldShowLeaseExpiredBanner(leaseExpiredNotice: boolean): boolean {
  return leaseExpiredNotice
}
